import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  AppBar,
  Toolbar,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MuiIconButton from '@mui/material/IconButton';
import { Country } from '../api/ordering';

interface MobileShippingPageProps {
  onBack: () => void;
  company: string;
  setCompany: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  street: string;
  setStreet: (value: string) => void;
  street2: string;
  setStreet2: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  province: string;
  setProvince: (value: string) => void;
  zip: string;
  setZip: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  countries: Country[];
  accept: boolean;
  setAccept: (value: boolean) => void;
  termsOpen: boolean;
  setTermsOpen: (value: boolean) => void;
  onSubmit: () => void;
  submitting: boolean;
}

export default function MobileShippingPage({
  onBack,
  company,
  setCompany,
  notes,
  setNotes,
  street,
  setStreet,
  street2,
  setStreet2,
  city,
  setCity,
  province,
  setProvince,
  zip,
  setZip,
  country,
  setCountry,
  countries,
  accept,
  setAccept,
  termsOpen,
  setTermsOpen,
  onSubmit,
  submitting
}: MobileShippingPageProps) {
  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default'
      }}
    >
      {/* Mobile Header */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ minHeight: 48, px: 2 }}>
          <MuiIconButton
            onClick={onBack}
            sx={{
              color: '#e6ac30',
              mr: 1,
              '&:hover': {
                bgcolor: 'rgba(230, 172, 48, 0.1)',
              }
            }}
            aria-label="Back to cart"
          >
            <ArrowBackIcon />
          </MuiIconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 900,
              color: '#e6ac30',
              fontSize: '1.1rem'
            }}
          >
            Shipping Details
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 0.5, // Further reduced top padding
          // Hide scrollbar but keep functionality
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          pb: 16, // Extra padding for bottom button area
        }}
      >
        <Stack spacing={2}>
          {/* Company Information */}
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Company Information
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Company Name"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
              <TextField
                label="Order Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Any special instructions or requirements..."
              />
            </Stack>
          </Paper>

          {/* Shipping Address */}
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Shipping Address
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Street Address"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                required
              />
              <TextField
                label="Address Line 2 (Optional)"
                value={street2}
                onChange={(e) => setStreet2(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Apartment, suite, etc."
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  label="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  required
                />
                <TextField
                  label="Province/State"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="ZIP/Postal Code"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  required
                />
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    options={countries}
                    autoHighlight
                    fullWidth
                    value={countries.find(c => c.id === country) || null}
                    onChange={(_, val) => setCountry(val ? String(val.id) : '')}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        size="small"
                        variant="outlined"
                        required
                      />
                    )}
                    size="small"
                  />
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {/* Terms and Conditions */}
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={accept}
                  onChange={(e) => setAccept(e.target.checked)}
                  sx={{
                    color: '#e6ac30',
                    '&.Mui-checked': {
                      color: '#e6ac30',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  I accept the{' '}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsOpen(true);
                    }}
                    sx={{ color: '#e6ac30', textDecoration: 'underline' }}
                  >
                    terms and conditions
                  </Link>
                </Typography>
              }
            />
          </Paper>
        </Stack>
      </Box>

      {/* Sticky Bottom Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 2,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          // Add padding at bottom to account for safe area on mobile devices
          pb: { xs: 3, sm: 2 },
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            onClick={onBack}
            variant="outlined"
            fullWidth
            sx={{
              borderColor: '#e6ac30',
              color: '#e6ac30',
              '&:hover': {
                borderColor: '#d4a52a',
                bgcolor: 'rgba(230, 172, 48, 0.1)',
              }
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={onSubmit}
            disabled={!accept || submitting}
            sx={{
              bgcolor: '#e6ac30',
              '&:hover': {
                bgcolor: '#d4a52a',
              },
              '&:disabled': {
                bgcolor: 'grey.300',
              }
            }}
          >
            {submitting ? 'Placing Order...' : 'Place Order'}
          </Button>
        </Stack>
      </Box>

      {/* Terms & Conditions Modal */}
      <Dialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Terms & Conditions</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Payment Terms: as pr. invoice.
            </Typography>
            <Typography variant="body2">
              To ensure you benefit from our most favorable payment terms, we require all invoices to be settled on time. If a payment is overdue by more than five working days, a late fee of £150 will be applied. An additional charge of £50 will be added for each subsequent five-working-day period that the invoice remains unpaid. Prompt payment is essential to maintain your preferential terms.
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Monthly Support & Licensing Fee:
            </Typography>
            <Typography variant="body2">
              £7 pr. device pr. month for SIM + TMS, charged monthly with direct debit.
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Delivery Terms:
            </Typography>
            <Typography variant="body2">
              Goods will be delivered to the shipping address provided by the Buyer in the order form.
            </Typography>
            <Typography variant="body2">
              Delivery will occur only after full payment of the invoice has been received.
            </Typography>
            <Typography variant="body2">
              Risk of loss or damage transfers to the Buyer upon delivery.
            </Typography>
            <Typography variant="body2">
              Estimated delivery times are provided as a guide and are not guaranteed. The Seller is not liable for any delays in delivery.
            </Typography>
            <Typography variant="body2">
              Delivery of Goods will be made to the address specified by the Buyer in the order.
            </Typography>
            <Typography variant="body2">
              Delivery times are estimates and are not guaranteed. The Seller shall not be liable for any delays in delivery.
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Warranty</Typography>
            <Typography variant="body2">
              The Seller warrants that all hardware Goods will be free from defects in materials and workmanship for a period of 12 months from the date of delivery.
            </Typography>
            <Typography variant="body2">
              This warranty does not cover damage caused by misuse, neglect, accident, or unauthorised modification.
            </Typography>
            <Typography variant="body2">
              The Seller's sole liability under this warranty is limited to repairing or replacing defective Goods.
            </Typography>
            <Typography variant="body2">Thank you for Choosing SynthiaX Ltd.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}